import React from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle, HiOutlineXCircle } from "react-icons/hi2";
import {
  isMeaningfulValue,
  isTruthyFlag,
  isHiddenByConfig,
  getParentsOf,
  formatFieldDisplayName,
} from "./utils/analysis";

const ACCENT_BORDER = {
  warning: "border-amber-200 bg-amber-50/60",
  success: "border-emerald-200 bg-emerald-50/60",
  info: "border-blue-200 bg-blue-50/60",
};

const ACCENT_LABEL = {
  warning: "text-amber-700",
  success: "text-emerald-700",
  info: "text-blue-700",
};

const FactCard = ({ label, badge, accent, wide, children }) => (
  <div
    className={`rounded-lg border px-3 py-2.5 ${
      accent ? ACCENT_BORDER[accent] : "border-gray-200 bg-white"
    } ${wide ? "sm:col-span-2" : ""}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div
        className={`text-[10px] uppercase tracking-wide ${
          accent ? ACCENT_LABEL[accent] : "text-gray-400"
        }`}
      >
        {label}
      </div>
      {badge !== null && badge !== undefined && (
        <span className="text-[10px] font-semibold bg-primary/10 text-primary px-1.5 py-0.5 rounded-md shrink-0 tabular-nums">
          {badge}
        </span>
      )}
    </div>
    {children && <div className="mt-1">{children}</div>}
  </div>
);

// Grades like `back` / `sales_proposition` hold a score; show it as a badge and keep
// the body for the explanation. Non-numeric values stay in the body instead.
const isNumericScore = (value) => !Number.isNaN(Number(value));

const TextValue = ({ value }) => (
  <div className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{value}</div>
);

const BoolValue = ({ value, t }) => (
  <div
    className={`flex items-center gap-1.5 text-sm font-medium ${
      value ? "text-success" : "text-danger"
    }`}
  >
    {value ? <HiOutlineCheckCircle size={16} /> : <HiOutlineXCircle size={16} />}
    <span>{value ? t("Oui") : t("Non")}</span>
  </div>
);

// `inverted` flips the colour scale for metrics where a high value is bad
// (frustration), without changing the displayed number.
const NumberValue = ({ value, max = 10, inverted = false }) => {
  const ratio = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  const good = inverted ? 1 - ratio : ratio;
  const bar_color =
    good >= 0.7 ? "bg-grade-high" : good >= 0.5 ? "bg-grade-medium" : "bg-grade-low";

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-1.5 rounded-full transition-all duration-500 ease-out ${bar_color}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-gray-500 tabular-nums shrink-0">
        {value}/{max}
      </span>
    </div>
  );
};

const CategoryValue = ({ value }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
    {value}
  </span>
);

// Fields declaring this card's field as their parent (`parent_fields`), rendered
// indented underneath it the way the legacy accordion did.
const LinkedChildren = ({ fields, analysis }) => {
  if (!fields || fields.length === 0) return null;
  return (
    <div className="mt-2 pl-3 border-l-2 border-gray-100 flex flex-col gap-1.5">
      {fields.map((child) => (
        <div key={child.name}>
          <div className="text-[10px] uppercase tracking-wide text-gray-400">
            {child.display_name || formatFieldDisplayName(child.name)}
          </div>
          <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
            {String(analysis?.[child.name])}
          </div>
        </div>
      ))}
    </div>
  );
};

const ExtractedFactsGrid = ({
  analysis,
  company_config,
  has_global_visibility,
  is_sale_purpose_call,
  is_outgoing_call,
}) => {
  const { t } = useTranslation();
  const a = analysis || {};
  const items = Array.isArray(a.items_breakdown) ? a.items_breakdown : [];
  const to_retrieve = company_config?.to_retrieve || [];
  const graded_keys = new Set(items.flatMap((row) => [row?.field_name, row?.key].filter(Boolean)));
  const linked_child_names = new Set(
    to_retrieve
      .filter((f) => getParentsOf(f).some((p) => graded_keys.has(p)))
      .map((f) => f?.name)
      .filter(Boolean)
  );
  const is_graded = (key) => graded_keys.has(key) || linked_child_names.has(key);

  const cards = [];
  const push = (card) => cards.push(card);

  // Missed opportunity — same gate as the legacy accordion.
  if (
    !is_graded("opportunity_description") &&
    a.is_opportunity_missed &&
    !a.is_sales_concluded &&
    isMeaningfulValue(a.opportunity_description)
  ) {
    push({
      key: "opportunity_description",
      label: t("analysis.missedOpportunityReason"),
      accent: "warning",
      wide: true,
      node: <TextValue value={a.opportunity_description} />,
    });
  }
  // `> 0` rather than "has a value": the legacy accordion treated 0 as "not computed"
  // for these three metrics, and so does the calls table.
  if (!is_graded("dynamism") && Number(a.dynamism) > 0) {
    push({
      key: "dynamism",
      label: t("analysis.dynamism"),
      node: (
        <>
          <NumberValue value={Number(a.dynamism)} />
          {isMeaningfulValue(a.dynamism_grade_explanation) && (
            <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed mt-1.5">
              {a.dynamism_grade_explanation}
            </div>
          )}
        </>
      ),
    });
  }
  // Stored on a 0-5 scale, displayed out of 10 like the legacy accordion did.
  if (!is_graded("call_structure_respect") && Number(a.call_structure_respect) > 0) {
    push({
      key: "call_structure_respect",
      label: t("analysis.callStructureRespect"),
      node: (
        <>
          <NumberValue value={Number(a.call_structure_respect) * 2} />
          {isMeaningfulValue(a.call_structure_respect_explanation) && (
            <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed mt-1.5">
              {a.call_structure_respect_explanation}
            </div>
          )}
        </>
      ),
    });
  }
  if (!is_graded("client_sentiment") && isMeaningfulValue(a.client_sentiment)) {
    push({
      key: "client_sentiment",
      label: t("analysis.clientSentiment"),
      node: <TextValue value={a.client_sentiment} />,
    });
  }
  if (!is_graded("frustration_grade") && Number(a.frustration_grade) > 0) {
    const frustration = Number(a.frustration_grade);
    push({
      key: "frustration_grade",
      label: t("analysis.frustrationGrade"),
      accent: frustration >= 5 ? "warning" : undefined,
      node: (
        <NumberValue
          value={frustration}
          inverted
        />
      ),
    });
  }
  if (!is_graded("frustration_theme") && isMeaningfulValue(a.frustration_theme)) {
    push({
      key: "frustration_theme",
      label: t("analysis.frustrationTheme"),
      node: <TextValue value={t(`analysis.frustrationThemes.${a.frustration_theme || "other"}`)} />,
    });
  }
  if (!is_graded("agent_tone") && isMeaningfulValue(a.agent_tone)) {
    push({
      key: "agent_tone",
      label: t("analysis.agentTone"),
      node: <TextValue value={a.agent_tone} />,
    });
  }
  if (!is_graded("customer_service_tone") && isMeaningfulValue(a.customer_service_tone)) {
    push({
      key: "customer_service_tone",
      label: t("analysis.customerServiceTone"),
      node: <TextValue value={a.customer_service_tone} />,
    });
  }
  if (
    !is_graded("back") &&
    (has_global_visibility || is_sale_purpose_call || is_outgoing_call) &&
    isMeaningfulValue(a.back)
  ) {
    push({
      key: "back",
      label: t("analysis.objectionHandling"),
      badge: isNumericScore(a.back) ? String(a.back) : undefined,
      node: isMeaningfulValue(a.back_grade_explanation) ? (
        <TextValue value={a.back_grade_explanation} />
      ) : (
        !isNumericScore(a.back) && <TextValue value={String(a.back)} />
      ),
    });
  }
  if (
    !is_graded("sales_proposition") &&
    (has_global_visibility || is_sale_purpose_call || is_outgoing_call) &&
    isMeaningfulValue(a.sales_proposition)
  ) {
    push({
      key: "sales_proposition",
      label: t("analysis.salesProposition"),
      badge: isNumericScore(a.sales_proposition) ? String(a.sales_proposition) : undefined,
      node: isMeaningfulValue(a.sales_proposition_grade_explanation) ? (
        <TextValue value={a.sales_proposition_grade_explanation} />
      ) : (
        !isNumericScore(a.sales_proposition) && <TextValue value={String(a.sales_proposition)} />
      ),
    });
  }
  if (
    !is_graded("client_company_name") &&
    has_global_visibility &&
    isMeaningfulValue(a.client_company_name)
  ) {
    push({
      key: "client_company_name",
      label: t("analysis.clientCompanyName"),
      node: <TextValue value={a.client_company_name} />,
    });
  }
  if (
    !is_graded("client_phone_number") &&
    has_global_visibility &&
    isMeaningfulValue(a.client_phone_number)
  ) {
    push({
      key: "client_phone_number",
      label: t("analysis.clientPhoneNumber"),
      node: <TextValue value={a.client_phone_number} />,
    });
  }
  if (
    !is_graded("was_product_not_found") &&
    has_global_visibility &&
    isTruthyFlag(a.was_product_not_found)
  ) {
    push({
      key: "product_not_found",
      label: `⚠ ${t("analysis.productNotFound")}`,
      accent: "warning",
      node: <TextValue value={a.product_asked_name || t("analysis.productNotFound")} />,
    });
  }
  if (
    !is_graded("is_product_out_of_stock") &&
    has_global_visibility &&
    isTruthyFlag(a.is_product_out_of_stock)
  ) {
    push({
      key: "is_product_out_of_stock",
      label: `⚠ ${t("analysis.outOfStock")}`,
      accent: "warning",
      node: <TextValue value={t("Oui")} />,
    });
  }
  if (!is_graded("upsell") && has_global_visibility && isMeaningfulValue(a.upsell)) {
    push({
      key: "upsell",
      label: t("analysis.upsell"),
      node: <TextValue value={a.upsell} />,
    });
  }
  if (
    !is_graded("is_completed_order") &&
    has_global_visibility &&
    isTruthyFlag(a.is_completed_order)
  ) {
    push({
      key: "is_completed_order",
      label: `✓ ${t("analysis.orderCompleted")}`,
      accent: "success",
      node: <TextValue value={t("Oui")} />,
    });
  }
  if (!is_graded("resolved") && has_global_visibility && isTruthyFlag(a.resolved)) {
    push({
      key: "resolved",
      label: `✓ ${t("analysis.resolved")}`,
      accent: "success",
      node: <TextValue value={t("Oui")} />,
    });
  }

  // Child fields are subject to the same value / hide_when_* filtering as their parent.
  const children_of = (parent_name) =>
    to_retrieve.filter((field) => {
      if (!getParentsOf(field).includes(parent_name)) return false;
      const value = a?.[field?.name];
      return isMeaningfulValue(value) && !isHiddenByConfig(field, value);
    });

  const render_custom_value = (field, value) => {
    switch (field?.type) {
      case "bool":
        return (
          <BoolValue
            value={isTruthyFlag(value)}
            t={t}
          />
        );
      case "number": {
        const numeric = Number(value);
        if (Number.isNaN(numeric)) return <TextValue value={String(value)} />;
        return <NumberValue value={numeric} />;
      }
      case "category":
        return <CategoryValue value={String(value)} />;
      default:
        return <TextValue value={String(value)} />;
    }
  };

  // Company-configured extraction fields, rendered by declared type so a bool stays
  // Oui/Non rather than a raw "0"/"1", and honouring the hide_when_* config.
  const custom_cards = to_retrieve
    .filter((field) => {
      const value = a?.[field?.name];
      if (!isMeaningfulValue(value)) return false;
      if (is_graded(field?.name)) return false;
      if (getParentsOf(field).length > 0) return false; // rendered under its parent
      return !isHiddenByConfig(field, value);
    })
    .map((field) => {
      const value = a[field.name];
      const linked = children_of(field.name);
      return {
        key: `custom-${field.name}`,
        label: field.display_name || formatFieldDisplayName(field.name),
        node: (
          <>
            {render_custom_value(field, value)}
            <LinkedChildren
              fields={linked}
              analysis={a}
            />
          </>
        ),
      };
    });

  let discount_card = null;
  if (
    !is_graded("product_discount_examples") &&
    has_global_visibility &&
    Array.isArray(a.product_discount_examples) &&
    a.product_discount_examples.length > 0
  ) {
    const lines = a.product_discount_examples
      .map((p) => {
        const parts = [];
        if (p?.name) parts.push(p.name);
        if (p?.price_with_discount) parts.push(`Avec remise: ${p.price_with_discount}`);
        if (p?.price_without_discount) parts.push(`Avant remise: ${p.price_without_discount}`);
        if (p?.discount_sentence) parts.push(p.discount_sentence);
        return parts.join(" · ");
      })
      .filter(Boolean);
    if (lines.length > 0) {
      discount_card = {
        key: "product_discount_examples",
        label: t("analysis.discountsPresented"),
        wide: true,
        node: <TextValue value={lines.join("\n")} />,
      };
    }
  }

  const all_cards = [...cards, ...custom_cards, ...(discount_card ? [discount_card] : [])];

  if (all_cards.length === 0) return null;

  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
        {t("Faits extraits")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {all_cards.map((card) => (
          <FactCard
            key={card.key}
            label={card.label}
            badge={card.badge}
            accent={card.accent}
            wide={card.wide}
          >
            {card.node}
          </FactCard>
        ))}
      </div>
    </div>
  );
};

export default ExtractedFactsGrid;
